import React from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Pagination,
  Select,
} from "@mui/material";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import LinearProgress from "@mui/material/LinearProgress";
import RefreshIcon from "@mui/icons-material/Refresh";
import { exportExpensesToPDF } from "../../utils/exportExpenses";

export default function ExpensesTable() {
  const auth = useAuth();
  const token = auth?.user?.token;
  const { t } = useTranslation();

  const [selectedPeriod, setSelectedPeriod] = React.useState("custom");
  const [startDate, setStartDate] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);
  const [selectedCategory, setSelectedCategory] = React.useState("");
  const [selectedCurrency, setSelectedCurrency] = React.useState("");
  const [selectedExpenseBy, setSelectedExpenseBy] = React.useState("");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const open = Boolean(anchorEl);
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const getDateRangeForPeriod = React.useCallback((period) => {
    const today = dayjs();

    switch (period) {
      case "daily":
        return { start: today, end: today };
      case "weekly":
        return { start: today.startOf("week"), end: today.endOf("week") };
      case "monthly":
        return { start: today.startOf("month"), end: today.endOf("month") };
      case "yearly":
        return { start: today.startOf("year"), end: today.endOf("year") };
      default:
        return { start: null, end: null };
    }
  }, []);

  const applyPeriod = React.useCallback(
    (period) => {
      setSelectedPeriod(period);
      setPage(1);

      if (period === "custom") {
        setStartDate(null);
        setEndDate(null);
        return;
      }

      const { start, end } = getDateRangeForPeriod(period);
      setStartDate(start);
      setEndDate(end);
    },
    [getDateRangeForPeriod]
  );

  React.useEffect(() => {
    setPage(1);
  }, [startDate, endDate, selectedCategory, selectedCurrency, selectedExpenseBy, selectedPeriod]);

  const formatDateParam = React.useCallback((value) => {
    if (!value || !dayjs(value).isValid()) return "";
    return dayjs(value).format("YYYY-MM-DD");
  }, []);

  const buildExpenseParams = React.useCallback(() => {
    const params = new URLSearchParams();
    const start = formatDateParam(startDate);
    const end = formatDateParam(endDate);
    if (start && end && dayjs(start).isAfter(dayjs(end))) {
      params.set("start_date", end);
      params.set("end_date", start);
    } else {
      if (start) params.set("start_date", start);
      if (end) params.set("end_date", end);
    }
    if (selectedCategory) params.set("category", selectedCategory);
    if (selectedCurrency) params.set("currency", selectedCurrency);
    if (selectedExpenseBy) params.set("expense_by", selectedExpenseBy);
    return params;
  }, [startDate, endDate, selectedCategory, selectedCurrency, selectedExpenseBy, formatDateParam]);

  const queryString = React.useMemo(() => {
    const params = buildExpenseParams();
    params.set("page", String(page));
    if (rowsPerPage !== -1) {
      params.set("page_size", String(rowsPerPage));
    } else {
      params.set("page_size", "all");
    }
    return params.toString();
  }, [buildExpenseParams, page, rowsPerPage]);

  const exportQueryString = React.useMemo(() => {
    const params = buildExpenseParams();
    params.set("page_size", "all");
    return params.toString();
  }, [buildExpenseParams]);

  const {
    data: expensesData,
    isLoading,
    isError,
  } = useFetchObjects(
    ["expenses", queryString],
    `shop/expenses/?${queryString}`,
    token
  );

  const { data: exportExpensesData } = useFetchObjects(
    ["expenses-export", exportQueryString],
    `shop/expenses/?${exportQueryString}`,
    token
  );

  // Fetch expense categories for filter dropdown
  const { data: categoriesData } = useFetchObjects(
    ["expense-categories", "expense-report-filter"],
    "shop/expense-categories?page_size=all",
    token
  );

  const { data: currenciesData } = useFetchObjects(
    ["currencies", "expense-report-filter"],
    "shop/currencies/?page_size=all",
    token
  );

  const categories = Array.isArray(categoriesData?.results)
    ? categoriesData.results
    : Array.isArray(categoriesData)
      ? categoriesData
      : [];
  const currencies = Array.isArray(currenciesData?.results)
    ? currenciesData.results
    : Array.isArray(currenciesData)
      ? currenciesData
      : [];

  const expenses = React.useMemo(() => {
    if (Array.isArray(expensesData?.results)) return expensesData.results;
    if (Array.isArray(expensesData)) return expensesData;
    return [];
  }, [expensesData]);
  const exportExpenses = React.useMemo(() => {
    if (Array.isArray(exportExpensesData?.results)) return exportExpensesData.results;
    if (Array.isArray(exportExpensesData)) return exportExpensesData;
    return expenses;
  }, [exportExpensesData, expenses]);
  const totalCount = Number(expensesData?.count ?? expenses.length);

  const expenseByOptions = React.useMemo(() => {
    const names = new Set();
    exportExpenses.forEach((expense) => {
      if (expense.expense_by) names.add(expense.expense_by);
    });
    return Array.from(names).sort();
  }, [exportExpenses]);

  // Calculate summary totals
  const summaryTotals = React.useMemo(() => {
    return exportExpenses.reduce((acc, expense) => {
      const amount = parseFloat(expense.amount) || 0;
      const currency = expense.currency_details?.title || "-";
      
      return {
        totalAmount: acc.totalAmount + amount,
        byCurrency: {
          ...acc.byCurrency,
          [currency]: (acc.byCurrency[currency] || 0) + amount,
        },
      };
    }, { totalAmount: 0, byCurrency: {} });
  }, [exportExpenses]);

  const summaryLabel = React.useMemo(() => {
    const entries = Object.entries(summaryTotals.byCurrency);
    if (entries.length === 0) return "0.00";
    return entries
      .map(([currency, amount]) => `${amount.toFixed(2)} ${currency}`)
      .join(", ");
  }, [summaryTotals]);

  const handleExportClick = (type) => {
    const today = new Date().toISOString().slice(0, 10);
    const base = `expenses_${today}`;
    if (type === "pdf") exportExpensesToPDF(exportExpenses, `${base}.pdf`);
    handleClose();
  };

  const resetPageAndSet = (setter) => (value) => {
    setSelectedPeriod("custom");
    setter(value);
    setPage(1);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <LinearProgress sx={{ width: "90%" }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" color="error">
          {t("ReportIndex.error")}{" "}
          <RefreshIcon sx={{ cursor: "pointer", verticalAlign: "middle" }} />
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Filters */}
      <Box sx={{ flexGrow: 1, mb: 5, m: "15px" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2,
                  alignItems: { xs: "stretch", md: "center" },
                }}
              >
                <FormControl fullWidth>
                  <Select
                    value={selectedPeriod}
                    onChange={(e) => applyPeriod(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="custom">
                      {t("ReportIndex.period_custom", "Custom Range")}
                    </MenuItem>
                    <MenuItem value="daily">
                      {t("ReportIndex.period_daily", "Daily")}
                    </MenuItem>
                    <MenuItem value="weekly">
                      {t("ReportIndex.period_weekly", "Weekly")}
                    </MenuItem>
                    <MenuItem value="monthly">
                      {t("ReportIndex.period_monthly", "Monthly")}
                    </MenuItem>
                    <MenuItem value="yearly">
                      {t("ReportIndex.period_yearly", "Yearly")}
                    </MenuItem>
                  </Select>
                </FormControl>
                <DatePicker
                  label={t("ReportIndex.datepicker_start")}
                  value={startDate}
                  onChange={resetPageAndSet(setStartDate)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <DatePicker
                  label={t("ReportIndex.datepicker_end")}
                  value={endDate}
                  onChange={resetPageAndSet(setEndDate)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <FormControl fullWidth>
                  <Select
                    value={selectedCategory}
                    onChange={(e) => {
                      setSelectedCategory(e.target.value);
                      setPage(1);
                    }}
                    displayEmpty
                  >
                    <MenuItem value="">{t("ReportIndex.all_categories")}</MenuItem>
                    {(categories || []).map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <Select
                    value={selectedCurrency}
                    onChange={(e) => {
                      setSelectedCurrency(e.target.value);
                      setPage(1);
                    }}
                    displayEmpty
                  >
                    <MenuItem value="">{t("ReportIndex.all_currencies", "All Currencies")}</MenuItem>
                    {currencies.map((currency) => (
                      <MenuItem key={currency.id} value={String(currency.id)}>
                        {currency.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <Select
                    value={selectedExpenseBy}
                    onChange={(e) => {
                      setSelectedExpenseBy(e.target.value);
                      setPage(1);
                    }}
                    displayEmpty
                  >
                    <MenuItem value="">{t("ReportIndex.all_expense_by", "All Expense By")}</MenuItem>
                    {expenseByOptions.map((expenseBy) => (
                      <MenuItem key={expenseBy} value={expenseBy}>
                        {expenseBy}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    setStartDate(null);
                    setEndDate(null);
                    setSelectedCategory("");
                    setSelectedCurrency("");
                    setSelectedExpenseBy("");
                    setPage(1);
                  }}
                  sx={{
                    whiteSpace: "nowrap",
                    width: { xs: "100%", md: "auto" },
                    flexShrink: 0,
                    height: 55,
                  }}
                >
                  {t("ReportIndex.btn_clear")}
                </Button>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", md: "flex-end" },
              }}
            >
              <FormControl
                sx={{ m: 1, minWidth: 200, width: { xs: "100%", md: "auto" } }}
              >
                <Box>
                  <Button
                    id="export-button"
                    aria-controls={open ? "export-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    onClick={handleClick}
                    variant="outlined"
                    fullWidth
                    sx={{
                      whiteSpace: "nowrap",
                      width: { xs: "100%", md: "auto" },
                      flexShrink: 0,
                      height: 55,
                    }}
                  >
                    {t("ReportIndex.btn_export")}
                  </Button>
                  <Menu
                    id="export-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{ "aria-labelledby": "export-button" }}
                  >
                    <MenuItem onClick={() => handleExportClick("excel")}>
                      {t("ReportIndex.export_excel")}
                    </MenuItem>
                    <MenuItem onClick={() => handleExportClick("pdf")}>
                      {t("ReportIndex.export_pdf")}
                    </MenuItem>
                    <MenuItem onClick={() => handleExportClick("none")}>
                      {t("ReportIndex.export_none")}
                    </MenuItem>
                  </Menu>
                </Box>
              </FormControl>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>

      <Divider />

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, ml: 2 }}>
        <Typography>{t("reports.rowsPerPage")}</Typography>
        <FormControl size="small">
          <Select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={30}>30</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
            <MenuItem value={-1}>{t("reports.all")}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Divider />

      {/* Expenses Table */}
      <TableContainer component={Paper}>
        <Table aria-label="expenses table">
          <TableHead>
            <TableRow>
              <TableCell colSpan={7} align="center">
                <Typography variant="h4">
                  {t("ReportIndex.expenses_tableTitle")}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t("ReportIndex.expenses_table_id")}</TableCell>
              <TableCell>{t("ReportIndex.expenses_table_category", "Expense Category")}</TableCell>
              <TableCell>{t("ReportIndex.expenses_table_expenseBy")}</TableCell>
              <TableCell>{t("ReportIndex.expenses_table_amount")}</TableCell>
              <TableCell>
                {t("ReportIndex.expenses_table_paymentDate")}
              </TableCell>
              <TableCell>{t("ReportIndex.expenses_table_notes")}</TableCell>
              <TableCell>{t("ReportIndex.expenses_table_createdAt")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {expenses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {t("expense.noExpensesFound", "No expenses found")}
                </TableCell>
              </TableRow>
            ) : expenses.map((expense) => (
              <TableRow key={expense.id}>
                <TableCell>{expense.id}</TableCell>
                <TableCell>{expense.category_details?.name || "-"}</TableCell>
                <TableCell>{expense.expense_by || "-"}</TableCell>
                <TableCell>
                  {expense.amount || "0"}{" "}
                  {expense.currency_details?.title || "-"}
                </TableCell>
                <TableCell>
                  {expense.payment_date
                    ? dayjs(expense.payment_date).format("YYYY-MM-DD")
                    : "-"}
                </TableCell>
                <TableCell>{expense.notes || "-"}</TableCell>
                <TableCell>
                  {expense.created_at
                    ? new Date(expense.created_at).toLocaleString()
                    : "-"}
                </TableCell>
              </TableRow>
            ))}
            {/* Summary Row */}
            <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
              <TableCell />
              <TableCell />
              <TableCell sx={{ fontWeight: 'bold' }}>{t('Summary')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {summaryLabel}
              </TableCell>
              <TableCell />
              <TableCell />
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {rowsPerPage !== -1 && (
        <Grid container justifyContent="center" alignItems="center" padding="10px">
          <Pagination
            count={Math.max(1, Math.ceil(totalCount / rowsPerPage))}
            page={Math.min(page, Math.max(1, Math.ceil(totalCount / rowsPerPage)))}
            onChange={(event, newPage) => setPage(newPage)}
            variant="outlined"
            color="primary"
            disabled={totalCount === 0}
          />
        </Grid>
      )}
    </>
  );
}
